import { useAuthStore } from '@/store/auth.store'
import { useTranslation } from 'react-i18next'
import type {TranslationMap} from "@/components/common/translations/types.ts";
import {postApiCompaniesCurrentTranslationsEntityTypeEntityIdAuto} from "@/api/generated/public/public.ts";
import type {LocalizationEntityType} from "@/api/model";

const FLAG_CODES: Record<string, string> = {
  en: 'gb', el: 'gr', cs: 'cz', uk: 'ua', he: 'il', zh: 'cn',
  fa: 'ir', sr: 'rs', ar: 'sa', da: 'dk', sv: 'se', et: 'ee', sl: 'si',
}

export interface ResolvedLanguage {
  code: string
  label: string
  flagCode: string
}

export function useResolvedLanguages(): ResolvedLanguage[] {
  const { i18n } = useTranslation()
  const supportedLanguages = useAuthStore((s) => s.supportedLanguages)
  const displayLang = i18n.resolvedLanguage ?? i18n.language

  return supportedLanguages.map((code) => {
    let label = code
    try {
      label = new Intl.DisplayNames([displayLang], { type: 'language' }).of(code) ?? code
    } catch { /* fall back to code */ }
    return { code, label, flagCode: FLAG_CODES[code.toLowerCase()] ?? code }
  })
}

export interface PropertyTranslationRequest {
  property: string
  /** All languages the user planned for this property */
  plannedLanguages: string[]
  /**
   * All translations that already exist for this property — union of manual
   * translations (from useEntityTranslations) and any previously auto-translated
   * values. Languages present here with a non-empty value are skipped.
   */
  existingTranslations: TranslationMap
}
export function triggerAutoTranslation({
                                               entityType,
                                               entityId,
                                               properties,
                                             }: {
  entityType: string
  entityId: string
  properties: PropertyTranslationRequest[]
}) {
  const meaningful = properties.filter((p) => p.plannedLanguages.length > 0)
  if (meaningful.length === 0) return

  const payload = meaningful
    .map((p) => ({
      property: p.property,
      languages: p.plannedLanguages.filter(
        (code) => !p.existingTranslations[code]?.trim(),
      ),
    }))
    .filter((p) => p.languages.length > 0)

  if (payload.length === 0) {
    return
  }

  postApiCompaniesCurrentTranslationsEntityTypeEntityIdAuto(
    entityType as LocalizationEntityType,
    entityId,
    {
      properties: payload.map((p) => ({
        property: p.property,
        languages: p.languages,
      })),
    },
  ).catch(() => {
    // intentionally silent — the backend logs translation errors to Sentry
  })
}
